'use client';

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Image from 'next/image';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import { HERO_IMAGES } from '@/lib/public/universe-visuals';
import { UNIVERSES } from '@/lib/public/universes';
import { PORTAL_COPY } from './portal-copy';

export type GalleryHandle = { select: (index: number) => void };
type Props = {
  paused: boolean;
  intro: boolean;
  onPause: () => void;
};

// The incoming and outgoing fades are included in each image's 2.5-second window.
const TRANSITION_MS = 500;
const ADVANCE_MS = 2500 - TRANSITION_MS;

const PortalCinematicGallery = forwardRef<GalleryHandle, Props>(function PortalCinematicGallery({ paused, intro, onPause }, ref) {
  const { locale } = useI18n();
  const copy = PORTAL_COPY[locale];
  const layers = useRef<(HTMLDivElement | null)[]>([]);
  const target = useRef(0);
  const opacities = useRef([1, 0, 0, 0]);
  const from = useRef([1, 0, 0, 0]);
  const transitionStart = useRef(0);
  const lastAdvance = useRef(0);
  const [active, setActive] = useState(0);
  const [reduced, setReduced] = useState(false);
  const [hidden, setHidden] = useState(false);
  const activeRef = useRef(0);
  const select = (index: number) => {
    const next = Math.max(0, Math.min(UNIVERSES.length - 1, index));
    if (target.current === next) return;
    from.current = [...opacities.current];
    target.current = next;
    transitionStart.current = performance.now();
    lastAdvance.current = transitionStart.current;
  };
  useImperativeHandle(ref, () => ({ select }), []);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(preference.matches);
    const visibility = () => setHidden(document.hidden);
    update(); visibility();
    preference.addEventListener('change', update);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      preference.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);

  useEffect(() => {
    if (!paused) lastAdvance.current = performance.now();
  }, [paused, intro, hidden, reduced]);

  useEffect(() => {
    if (hidden || intro) return;
    let frame = 0;
    let previousProgress = -1;
    let previousTarget = -1;
    const render = (now: number) => {
      if (!paused && !reduced && now - lastAdvance.current >= ADVANCE_MS) {
        from.current = [...opacities.current];
        target.current = (target.current + 1) % UNIVERSES.length;
        transitionStart.current = now;
        lastAdvance.current = now;
      }
      const progress = paused || reduced ? 1 : Math.min(1, Math.max(0, (now - transitionStart.current) / TRANSITION_MS));
      if (progress !== previousProgress || target.current !== previousTarget) {
        const eased = progress * progress * (3 - 2 * progress);
        layers.current.forEach((layer, index) => {
          if (!layer) return;
          const incoming = index === target.current;
          const opacity = from.current[index] * (1 - eased) + (incoming ? eased : 0);
          const distance = incoming ? opacity - 1 : 1 - opacity;
          opacities.current[index] = opacity;
          layer.style.opacity = String(opacity);
          layer.style.visibility = opacity > 0 ? 'visible' : 'hidden';
          layer.style.transform = reduced || paused ? 'none' : `translate3d(${distance * -2}%, 0, ${distance * 50}px) scale(${1.055 + distance * .055})`;
        });
        const current = opacities.current.indexOf(Math.max(...opacities.current));
        if (current !== activeRef.current) { activeRef.current = current; setActive(current); }
        previousProgress = progress;
        previousTarget = target.current;
      }
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [hidden, intro, paused, reduced]);

  const show = (index: number) => select((index + 4) % 4);
  return <>
    <div className={`u-gallery${paused || reduced || hidden || intro ? ' is-paused' : ''}`} aria-hidden="true">
      {UNIVERSES.map((universe, index) => <div className="u-gallery-layer" key={universe.id} ref={(node) => { layers.current[index] = node; }} style={{ opacity: index === 0 ? 1 : 0 }}>
        <Image src={universe.id === 'r-node' ? '/assets/universes/portal-rnode-20261015.webp' : HERO_IMAGES[universe.id].src}
          width={1920} height={1080} sizes="100vw" quality={75} loading="eager" alt="" fetchPriority={index === 0 ? 'high' : 'low'} decoding="async" draggable={false} />
      </div>)}
      <div className="u-gallery-shade" />
      <div className="u-gallery-grain" />
    </div>
    <aside className="u-gallery-caption" aria-label={copy.gallery}>
      <div className="u-gallery-caption-top"><span>0{active + 1}<span> / 04</span></span><span>{UNIVERSES[active].name}</span></div>
      <p className="u-gallery-chapter" key={active}>{copy.chapters[active]}</p>
      <div className="u-gallery-controls">
        <div className="u-gallery-dots">{UNIVERSES.map((u, i) => <button key={u.id} type="button" aria-label={`${copy.show} ${u.name}`} aria-pressed={active === i} onClick={() => show(i)}><span /></button>)}</div>
        <div className="u-gallery-transport">
          <button type="button" aria-label={copy.previous} onClick={() => show(active - 1)}>←</button>
          <button type="button" aria-label={paused ? copy.play : copy.pause} aria-pressed={paused} onClick={onPause}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">{paused ? <path d="M5 2.5 13 8l-8 5.5Z" /> : <path d="M4 3h3v10H4zm5 0h3v10H9z" />}</svg>
          </button>
          <button type="button" aria-label={copy.next} onClick={() => show(active + 1)}>→</button>
        </div>
      </div>
    </aside>
  </>;
});
export default PortalCinematicGallery;

